/** Role keys used in matrix and user records */
export const companyRoleKeys = {
  tenant_admin: 'Tenant Admin',
  scheduler: 'Scheduler',
  auditor: 'Auditor',
  noc_operator: 'NOC Operator',
  viewer: 'Viewer',
};

const ROLE_ORDER = ['tenant_admin', 'scheduler', 'auditor', 'noc_operator', 'viewer'];

function pickRole(i) {
  return ROLE_ORDER[i % ROLE_ORDER.length];
}

export const usersList = Array.from({ length: 25 }, (_, i) => {
  const invited = i >= 18 && i < 22;
  const suspended = i >= 22;
  return {
    id: `usr-${2000 + i}`,
    displayName: `User ${i + 1}`,
    email: `user${i + 1}@acme.example`,
    role: pickRole(i),
    roleLabel: companyRoleKeys[pickRole(i)] || pickRole(i),
    department: ['Sales', 'Engineering', 'HR', 'Ops'][i % 4],
    departmentId: `dept-${i % 8}`,
    employeeId: `EMP-${2100 + i}`,
    status: invited ? 'pending' : suspended ? 'suspended' : 'active',
    channels: i % 3 === 0 ? ['Teams', 'Web'] : i % 3 === 1 ? ['Teams', 'WhatsApp'] : ['Teams'],
    lastLoginAt: invited ? null : new Date(Date.now() - i * 86400000).toISOString(),
    mfaEnabled: i % 5 !== 0,
  };
});

export const userDetailTemplate = {
  phone: '+968 9000 0000',
  jobTitle: 'Meeting Coordinator',
  preferredLanguage: 'en',
  managerId: 'usr-2005',
  groups: ['All Employees', 'Sales Gulf'],
  licenses: ['MeetingHub Pro'],
  ownedMeetingsCount: 14,
  access: {
    teams: true,
    whatsapp: true,
    webPortal: true,
  },
  notificationPrefs: {
    teams: true,
    whatsapp: false,
    email: true,
    quietStart: '22:00',
    quietEnd: '07:00',
  },
};

export const userDetail = {
  ...usersList[0],
  ...userDetailTemplate,
};

/** Permission row per module for matrix UI */
export const roleMatrixModules = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'users', label: 'Users' },
  { id: 'departments', label: 'Departments' },
  { id: 'meetings', label: 'Meetings' },
  { id: 'mom', label: 'MOM' },
  { id: 'templates', label: 'Templates' },
  { id: 'notifications', label: 'Notifications' },
  { id: 'reports', label: 'Reports' },
  { id: 'audit', label: 'Audit' },
  { id: 'settings', label: 'Settings' },
];

/** Core + custom roles; custom roles have locked: false */
export const roleMatrixRolesSeed = [
  {
    id: 'tenant_admin',
    label: 'Tenant Admin',
    locked: true,
    description: 'Full administrative access including billing and security settings.',
    userCount: 2,
  },
  {
    id: 'scheduler',
    label: 'Scheduler',
    locked: true,
    description: 'Create and manage meetings across departments.',
    userCount: 8,
  },
  {
    id: 'auditor',
    label: 'Auditor',
    locked: true,
    description: 'Read-only plus transcript and audit visibility.',
    userCount: 3,
  },
  {
    id: 'noc_operator',
    label: 'NOC Operator',
    locked: true,
    description: 'Operational monitoring and incident response.',
    userCount: 4,
  },
  {
    id: 'viewer',
    label: 'Viewer',
    locked: true,
    description: 'Read-only dashboard and calendar visibility.',
    userCount: 6,
  },
];

/** key: `${moduleId}:${roleId}` -> { view, create, edit, approve } */
export function buildDefaultRolePermissions() {
  const perms = {};
  const all = { view: true, create: true, edit: true, approve: true };
  const sched = { view: true, create: true, edit: true, approve: true };
  const read = { view: true, create: false, edit: false, approve: false };
  const momApprovers = { view: true, create: true, edit: true, approve: true };
  roleMatrixModules.forEach((m) => {
    perms[`${m.id}:tenant_admin`] = { ...all };
    perms[`${m.id}:scheduler`] =
      m.id === 'settings' || m.id === 'audit'
        ? { view: true, create: false, edit: false, approve: false }
        : { ...sched };
    perms[`${m.id}:auditor`] =
      m.id === 'mom' || m.id === 'audit' || m.id === 'reports'
        ? { view: true, create: false, edit: false, approve: false }
        : m.id === 'meetings'
          ? { view: true, create: false, edit: false, approve: false }
          : read;
    perms[`${m.id}:noc_operator`] =
      m.id === 'meetings' || m.id === 'notifications' || m.id === 'dashboard'
        ? { view: true, create: true, edit: true, approve: false }
        : read;
    perms[`${m.id}:viewer`] = { ...read };
    if (m.id === 'mom') {
      perms[`${m.id}:scheduler`] = { ...momApprovers };
    }
  });
  return perms;
}

export const roleMatrixPermissionsSeed = buildDefaultRolePermissions();

export const userActivitySeed = Array.from({ length: 20 }, (_, i) => ({
  id: `act-${i}`,
  at: new Date(Date.now() - i * 3600000).toISOString(),
  action: ['booked', 'rescheduled', 'cancelled', 'joined', 'exported'][i % 5],
  meetingTitle: `Weekly sync ${20 - i}`,
  result: i % 7 === 0 ? 'failed' : 'success',
}));

/** Access tab: module-level checkboxes (read-only), derived from role */
export const accessModuleMatrix = [
  { module: 'Meetings', view: true, create: true, edit: true, approve: false },
  { module: 'Transcripts', view: true, create: false, edit: false, approve: true },
  { module: 'Users', view: true, create: false, edit: false, approve: false },
  { module: 'Reports', view: true, create: false, edit: false, approve: false },
];

/** Legacy matrix for old API consumers */
export const roleMatrix = [
  { permission: 'manage_integrations', tenant_admin: true, scheduler: false, auditor: false, viewer: false },
  { permission: 'schedule_meetings', tenant_admin: true, scheduler: true, auditor: false, viewer: false },
  { permission: 'view_transcripts', tenant_admin: true, scheduler: true, auditor: true, viewer: true },
  { permission: 'approve_mom', tenant_admin: true, scheduler: true, auditor: false, viewer: false },
];
